import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../../supabaseClient'
import { useAuth } from '../../context/AuthContext'
import { uploadFile, getDownloadUrl, deleteFile } from '../../r2Client'
import {
  Plus, X, Film, Image, FileText, Link as LinkIcon,
  CheckCircle2, Clock, AlertCircle, Archive,
  MessageSquare, ChevronRight, Send, Check,
  Upload, Users, RotateCcw, Copy, AlertTriangle, Trash2,
} from 'lucide-react'

/* ─── helpers ─── */
const statusConfig = {
  in_revisione:      { label: 'In revisione', color: 'bg-amber-500/10 text-amber-400',   icon: Clock },
  approvato:         { label: 'Approvato',     color: 'bg-emerald-500/10 text-emerald-400', icon: CheckCircle2 },
  richiede_modifiche:{ label: 'Modifiche',     color: 'bg-red-500/10 text-red-400',       icon: AlertCircle },
  archiviato:        { label: 'Archiviato',    color: 'bg-gray-700 text-gray-500',         icon: Archive },
}

const tipoIcon = (tipo) => {
  if (tipo === 'video')   return <Film size={16} className="text-blue-400" />
  if (tipo === 'immagine') return <Image size={16} className="text-violet-400" />
  if (tipo === 'pdf')     return <FileText size={16} className="text-amber-400" />
  return <LinkIcon size={16} className="text-gray-400" />
}

const fmtTime = (sec) => {
  if (!sec && sec !== 0) return null
  const m = Math.floor(sec / 60)
  const s = Math.floor(sec % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

/* Ordina i commenti: prima quelli con timecode (crescente), poi quelli generali (per data) */
const sortCommenti = (arr) => [...arr].sort((a, b) => {
  const ta = a.timecode_sec, tb = b.timecode_sec
  if (ta == null && tb == null) return new Date(a.created_at) - new Date(b.created_at)
  if (ta == null) return 1
  if (tb == null) return -1
  if (ta !== tb) return ta - tb
  return new Date(a.created_at) - new Date(b.created_at)
})

/* Rende il testo con link cliccabili (http/https) */
const renderTextWithLinks = (text) => {
  if (!text) return null
  const parts = text.split(/(https?:\/\/[^\s]+)/g)
  return parts.map((part, i) =>
    /^https?:\/\//.test(part) ? (
      <a key={i} href={part} target="_blank" rel="noopener noreferrer"
        onClick={e => e.stopPropagation()}
        className="underline break-all hover:opacity-80">{part}</a>
    ) : part
  )
}

/* ─── Watermark overlay ─── */
const Watermark = ({ nome }) => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden select-none z-10">
    {Array.from({ length: 6 }).map((_, row) =>
      Array.from({ length: 4 }).map((_, col) => (
        <div
          key={`${row}-${col}`}
          className="absolute text-white/10 text-xs font-bold whitespace-nowrap"
          style={{
            top: `${row * 18 + 5}%`,
            left: `${col * 28 - 5}%`,
            transform: 'rotate(-30deg)',
            fontSize: '11px',
            letterSpacing: '0.05em',
          }}
        >
          {nome} · CONFIDENZIALE
        </div>
      ))
    )}
  </div>
)

/* ─── StatusBadge ─── */
const StatusBadge = ({ status }) => {
  const cfg = statusConfig[status] || statusConfig.in_revisione
  const Icon = cfg.icon
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full ${cfg.color}`}>
      <Icon size={11} /> {cfg.label}
    </span>
  )
}

/* ─── NuovaRevisioneModal ─── */
const NuovaRevisioneModal = ({ onClose, onSaved, myId }) => {
  const [step, setStep] = useState(1) // 1=info, 2=file, 3=destinatari, 4=link
  const [publicLink, setPublicLink] = useState(null)
  const [copied, setCopied] = useState(false)
  const [titolo, setTitolo] = useState('')
  const [descrizione, setDescrizione] = useState('')
  const [tipo, setTipo] = useState('video')
  const [link, setLink] = useState('')
  const [file, setFile] = useState(null)
  const [progetti, setProgetti] = useState([])
  const [selectedProject, setSelectedProject] = useState('')
  const [members, setMembers] = useState([])
  const [selectedMembers, setSelectedMembers] = useState([])
  const [visibilita, setVisibilita] = useState('progetto') // 'progetto' | 'specifici'
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [saving, setSaving] = useState(false)
  const inputRef = useRef()

  useEffect(() => {
    supabase.from('progetti').select('id, titolo, colore').order('titolo')
      .then(({ data }) => setProgetti(data || []))
  }, [])

  useEffect(() => {
    if (!selectedProject) { setMembers([]); return }
    supabase.from('project_members')
      .select('user_id, profiles(id, full_name, nickname, avatar_url, role)')
      .eq('project_id', selectedProject)
      .then(({ data }) => {
        const list = (data || []).map(d => d.profiles).filter(p => p && p.id !== myId)
        setMembers(list)
      })
  }, [selectedProject, myId])

  const toggleMember = (id) =>
    setSelectedMembers(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])

  const handleSubmit = async () => {
    if (!titolo.trim() || !selectedProject) return
    setSaving(true)
    try {
      let fileUrl = link
      let fileR2Key = null

      if (tipo !== 'link' && file) {
        setUploading(true)
        const ts = Date.now()
        const key = `revisioni/${selectedProject}/${ts}_${file.name.replace(/\s+/g, '_')}`
        await uploadFile(key, file, setProgress)
        fileR2Key = key
        fileUrl = await getDownloadUrl(key)
        setUploading(false)
      }

      // Crea revisione
      const { data: rev, error } = await supabase.from('revisioni').insert({
        progetto_id: selectedProject,
        inviato_da: myId,
        titolo: titolo.trim(),
        descrizione: descrizione.trim() || null,
        tipo,
        file_r2_key: fileR2Key,
        file_url: fileUrl || null,
        visibilita,
      }).select().single()

      if (error) throw error

      // Destinatari da notificare: tutti i membri (progetto) o solo i selezionati (specifici)
      const destinatari = visibilita === 'progetto'
        ? members.map(m => m.id)
        : selectedMembers

      // Per le revisioni 'specifici' salva anche la lista destinatari (governa la visibilità)
      if (visibilita === 'specifici' && selectedMembers.length > 0) {
        await supabase.from('revisione_destinatari').insert(
          selectedMembers.map(uid => ({ revisione_id: rev.id, user_id: uid }))
        )
      }

      // Notifica
      if (destinatari.length > 0) {
        await supabase.from('notifiche').insert(
          destinatari.map(uid => ({
            user_id: uid,
            tipo: 'revisione_ricevuta',
            titolo: titolo.trim(),
            body: descrizione.trim() || null,
            link: '/client/revisioni',
          }))
        )
      }

      // Genera link pubblico
      const publicUrl = `${window.location.origin}/r/${rev.public_token}`
      setPublicLink(publicUrl)
      setStep(4)
      onSaved()
    } catch (err) {
      console.error(err)
      alert('Errore: ' + err.message)
    }
    setSaving(false)
  }

  const handleCopyLink = () => {
    if (!publicLink) return
    navigator.clipboard.writeText(publicLink)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const canNext1 = titolo.trim() && selectedProject
  const canNext2 = tipo === 'link' ? link.trim() : !!file

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-lg flex flex-col" style={{ maxHeight: '90vh' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800 flex-shrink-0">
          <h2 className="text-white font-semibold">Nuova revisione</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X size={18} /></button>
        </div>

        {/* Stepper */}
        {step < 4 && (
          <div className="flex items-center gap-2 px-6 py-3 border-b border-gray-800 flex-shrink-0">
            {['Info', 'Contenuto', 'Destinatari'].map((s, i) => (
              <div key={s} className="flex items-center gap-2">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${step > i + 1 ? 'bg-violet-600 text-white' : step === i + 1 ? 'bg-violet-600 text-white' : 'bg-gray-800 text-gray-500'}`}>
                  {step > i + 1 ? <Check size={12} /> : i + 1}
                </div>
                <span className={`text-xs ${step === i + 1 ? 'text-white' : 'text-gray-600'}`}>{s}</span>
                {i < 2 && <ChevronRight size={12} className="text-gray-700" />}
              </div>
            ))}
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {/* Step 1 — Info */}
          {step === 1 && (
            <>
              <div>
                <label className="text-xs text-gray-500 mb-1.5 block">Titolo *</label>
                <input value={titolo} onChange={e => setTitolo(e.target.value)}
                  placeholder="Es. Home page v2 — cut finale"
                  className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-violet-500 transition-colors" />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1.5 block">Progetto *</label>
                <select value={selectedProject} onChange={e => setSelectedProject(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-violet-500 transition-colors">
                  <option value="">Seleziona progetto...</option>
                  {progetti.map(p => <option key={p.id} value={p.id}>{p.titolo}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1.5 block">Tipo contenuto</label>
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { v: 'video', label: 'Video', icon: Film },
                    { v: 'immagine', label: 'Immagine', icon: Image },
                    { v: 'pdf', label: 'PDF', icon: FileText },
                    { v: 'link', label: 'Link', icon: LinkIcon },
                  ].map(({ v, label, icon: Icon }) => (
                    <button key={v} onClick={() => setTipo(v)}
                      className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border text-xs transition-colors ${tipo === v ? 'border-violet-500 bg-violet-600/10 text-violet-300' : 'border-gray-700 text-gray-500 hover:border-gray-600'}`}>
                      <Icon size={18} /> {label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1.5 block">Note per il cliente (opzionale)</label>
                <textarea value={descrizione} onChange={e => setDescrizione(e.target.value)}
                  rows={3} placeholder="Cosa deve guardare? Cosa aspettarti come feedback?"
                  className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-violet-500 transition-colors resize-none" />
              </div>
            </>
          )}

          {/* Step 2 — Contenuto */}
          {step === 2 && (
            <>
              {tipo === 'link' ? (
                <div>
                  <label className="text-xs text-gray-500 mb-1.5 block">URL *</label>
                  <input value={link} onChange={e => setLink(e.target.value)}
                    placeholder="https://..."
                    className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-violet-500 transition-colors" />
                </div>
              ) : (
                <div>
                  <label className="text-xs text-gray-500 mb-1.5 block">File *</label>
                  <div
                    onClick={() => inputRef.current?.click()}
                    className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-colors ${file ? 'border-violet-500 bg-violet-600/5' : 'border-gray-700 hover:border-gray-600'}`}>
                    {file ? (
                      <>
                        <div className="flex items-center justify-center mb-2">{tipoIcon(tipo)}</div>
                        <p className="text-white text-sm font-medium truncate">{file.name}</p>
                        <p className="text-gray-500 text-xs mt-1">{(file.size / (1024 * 1024)).toFixed(1)} MB</p>
                        {uploading && (
                          <div className="mt-3">
                            <div className="w-full bg-gray-700 rounded-full h-1.5">
                              <div className="bg-violet-500 h-1.5 rounded-full transition-all" style={{ width: `${progress}%` }} />
                            </div>
                            <p className="text-xs text-gray-500 mt-1">{progress}%</p>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <Upload size={24} className="text-gray-600 mx-auto mb-3" />
                        <p className="text-gray-400 text-sm">Clicca per selezionare</p>
                        <p className="text-gray-600 text-xs mt-1">Nessun limite di dimensione</p>
                      </>
                    )}
                  </div>
                  <input
                    ref={inputRef}
                    type="file"
                    className="hidden"
                    accept={tipo === 'video' ? 'video/*' : tipo === 'immagine' ? 'image/*' : 'application/pdf'}
                    onChange={e => setFile(e.target.files[0] || null)}
                  />
                  {file && /\.(mov|avi|mxf|prores)$/i.test(file.name) && (
                    <div className="mt-3 flex items-start gap-2.5 bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3">
                      <AlertTriangle size={15} className="text-amber-400 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-amber-300/90 leading-relaxed">
                        I file <strong>.{file.name.split('.').pop().toLowerCase()}</strong> sono lenti da riprodurre nel browser: il cliente dovrà scaricare tutto il video prima che parta.
                        Per le revisioni esporta in <strong>MP4 H.264</strong> (in Premiere/Resolve è il preset standard) — parte subito anche se pesante.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* Step 4 — Link pubblico */}
          {step === 4 && (
            <div className="text-center py-4">
              <div className="w-14 h-14 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 size={26} className="text-emerald-400" />
              </div>
              <h3 className="text-white font-semibold mb-1">Revisione inviata!</h3>
              <p className="text-gray-500 text-sm mb-5">Condividi questo link — funziona anche per chi non è iscritto.</p>
              <div className="flex items-center gap-2 bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 mb-3">
                <span className="flex-1 text-sm text-gray-300 truncate text-left">{publicLink}</span>
                <button onClick={handleCopyLink} className="flex-shrink-0 flex items-center gap-1.5 text-xs text-violet-400 hover:text-violet-300 transition-colors">
                  {copied ? <Check size={13} /> : <Copy size={13} />}
                  {copied ? 'Copiato!' : 'Copia'}
                </button>
              </div>
            </div>
          )}

          {/* Step 3 — Destinatari */}
          {step === 3 && (
            <>
              {/* Scelta visibilità */}
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setVisibilita('progetto')}
                  className={`flex flex-col items-center gap-1.5 px-3 py-3 rounded-xl border text-sm font-medium transition-colors ${visibilita === 'progetto' ? 'bg-violet-600/20 border-violet-500 text-violet-300' : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-600'}`}
                >
                  <Users size={16} />
                  <span>Tutto il progetto</span>
                  <span className="text-xs font-normal opacity-60 text-center">Anche chi entra dopo</span>
                </button>
                <button
                  type="button"
                  onClick={() => setVisibilita('specifici')}
                  className={`flex flex-col items-center gap-1.5 px-3 py-3 rounded-xl border text-sm font-medium transition-colors ${visibilita === 'specifici' ? 'bg-violet-600/20 border-violet-500 text-violet-300' : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-600'}`}
                >
                  <Check size={16} />
                  <span>Persone specifiche</span>
                  <span className="text-xs font-normal opacity-60 text-center">Solo chi scegli tu</span>
                </button>
              </div>

              {visibilita === 'progetto' ? (
                <p className="text-xs text-gray-500 bg-gray-800/50 rounded-xl px-4 py-3">
                  Tutti i membri del progetto <span className="text-violet-400">{progetti.find(p => p.id === selectedProject)?.titolo}</span> vedranno questa revisione, inclusi quelli aggiunti in futuro. Chi lascia il progetto smette di vederla.
                </p>
              ) : (
                <>
              <p className="text-xs text-gray-500">
                Seleziona i membri del progetto <span className="text-violet-400">{progetti.find(p => p.id === selectedProject)?.titolo}</span> che riceveranno questa revisione.
              </p>
              {members.length === 0 ? (
                <div className="text-center py-6">
                  <Users size={28} className="text-gray-700 mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">Nessun membro in questo progetto.</p>
                  <p className="text-gray-600 text-xs mt-1">Invita prima i clienti tramite link.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {members.map(m => {
                    const nome = m.nickname || m.full_name || 'Utente'
                    const selected = selectedMembers.includes(m.id)
                    return (
                      <button key={m.id} onClick={() => toggleMember(m.id)}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border transition-colors ${selected ? 'border-violet-500 bg-violet-600/10' : 'border-gray-700 hover:border-gray-600'}`}>
                        <div className="w-8 h-8 rounded-full bg-violet-600 flex items-center justify-center text-white text-sm font-bold flex-shrink-0 overflow-hidden">
                          {m.avatar_url
                            ? <img src={m.avatar_url} alt={nome} className="w-full h-full object-cover" />
                            : nome[0]?.toUpperCase()}
                        </div>
                        <div className="flex-1 text-left">
                          <p className="text-white text-sm font-medium">{nome}</p>
                          <p className="text-gray-500 text-xs">{m.role === 'client' ? 'Cliente' : 'Membro'}</p>
                        </div>
                        {selected && <Check size={16} className="text-violet-400 flex-shrink-0" />}
                      </button>
                    )
                  })}
                </div>
              )}
                </>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-800 flex-shrink-0">
          {step < 4 ? (
            <>
              <button onClick={step === 1 ? onClose : () => setStep(s => s - 1)}
                className="text-sm text-gray-400 hover:text-white transition-colors">
                {step === 1 ? 'Annulla' : '← Indietro'}
              </button>
              {step < 3 ? (
                <button
                  onClick={() => setStep(s => s + 1)}
                  disabled={step === 1 ? !canNext1 : !canNext2}
                  className="bg-violet-600 hover:bg-violet-500 disabled:opacity-40 text-white text-sm font-medium px-5 py-2.5 rounded-xl transition-colors">
                  Avanti →
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={saving}
                  className="flex items-center gap-2 bg-violet-600 hover:bg-violet-500 disabled:opacity-40 text-white text-sm font-medium px-5 py-2.5 rounded-xl transition-colors">
                  {saving ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <Send size={14} />}
                  Invia revisione
                </button>
              )}
            </>
          ) : (
            <button onClick={onClose}
              className="ml-auto bg-gray-800 hover:bg-gray-700 text-white text-sm font-medium px-5 py-2.5 rounded-xl transition-colors">
              Chiudi
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

/* ─── ViewerModal ─── */
const ViewerModal = ({ revisione, myId, onClose, onStatusChange }) => {
  const [url, setUrl] = useState(null)
  const [commenti, setCommenti] = useState([])
  const [testo, setTesto] = useState('')
  const [timecode, setTimecode] = useState(null)
  const [sending, setSending] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [editText, setEditText] = useState('')
  const [currentTime, setCurrentTime] = useState(0)
  const videoRef = useRef(null)
  const { profile } = useAuth()

  const isTeam = profile?.role === 'admin' || profile?.role === 'member'

  useEffect(() => {
    // URL del media
    if (revisione.file_r2_key) {
      getDownloadUrl(revisione.file_r2_key).then(setUrl)
    } else if (revisione.file_url) {
      setUrl(revisione.file_url)
    }

    // Commenti
    fetchCommenti()

    // Marca come visto
    if (!isTeam) {
      supabase.from('revisione_destinatari')
        .update({ visto_at: new Date().toISOString() })
        .eq('revisione_id', revisione.id)
        .eq('user_id', myId)
        .is('visto_at', null)
    }
  }, [revisione.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCommenti = async () => {
    const { data } = await supabase
      .from('revisione_commenti')
      .select('*, autore:autore_id(full_name, nickname, avatar_url, role), autore_nome_guest')
      .eq('revisione_id', revisione.id)
      .order('created_at', { ascending: true })
    setCommenti(sortCommenti(data || []))
  }

  const handleEditComment = async (id) => {
    if (!editText.trim()) return
    const { error } = await supabase.from('revisione_commenti')
      .update({ testo: editText.trim() }).eq('id', id)
    if (!error) {
      setCommenti(prev => prev.map(c => c.id === id ? { ...c, testo: editText.trim() } : c))
      setEditingId(null); setEditText('')
    }
  }

  const handleSendComment = async () => {
    if (!testo.trim() || sending) return
    setSending(true)
    const { data: newC } = await supabase.from('revisione_commenti').insert({
      revisione_id: revisione.id,
      autore_id: myId,
      testo: testo.trim(),
      timecode_sec: timecode,
    }).select('*, autore:autore_id(full_name, nickname, avatar_url, role)').single()

    if (newC) setCommenti(prev => sortCommenti([...prev, newC]))

    // Notifica al mittente se sono un destinatario
    if (!isTeam) {
      await supabase.from('notifiche').insert({
        user_id: revisione.inviato_da,
        tipo: 'commento_revisione',
        titolo: revisione.titolo,
        body: testo.trim().substring(0, 80),
        link: '/revisioni',
      })
    }

    setTesto('')
    setTimecode(null)
    setSending(false)
  }

  const handleTimecodeClick = (sec) => {
    if (videoRef.current) {
      videoRef.current.currentTime = sec
      videoRef.current.play()
    }
  }

  const handleStatusChange = async (newStatus) => {
    await supabase.from('revisioni').update({ status: newStatus }).eq('id', revisione.id)
    // Notifica al mittente
    await supabase.from('notifiche').insert({
      user_id: revisione.inviato_da,
      tipo: newStatus === 'approvato' ? 'revisione_approvata' : 'revisione_modifiche',
      titolo: revisione.titolo,
      body: newStatus === 'approvato' ? 'Il cliente ha approvato.' : 'Il cliente richiede modifiche.',
      link: '/revisioni',
    })
    onStatusChange(revisione.id, newStatus)
  }

  const nome = profile?.nickname || profile?.full_name || 'Cliente'
  const isVideo = revisione.tipo === 'video'
  const isImage = revisione.tipo === 'immagine'
  const isPdf = revisione.tipo === 'pdf'
  const isLink = revisione.tipo === 'link'

  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex overflow-hidden" onClick={onClose}>
      <div className="flex w-full h-full" onClick={e => e.stopPropagation()}>

        {/* Media area */}
        <div className="flex-1 flex flex-col bg-black relative">
          {/* Top bar */}
          <div className="flex items-center justify-between px-4 py-3 bg-gray-900/80 backdrop-blur z-20 flex-shrink-0">
            <div className="flex items-center gap-3 min-w-0">
              {tipoIcon(revisione.tipo)}
              <div className="min-w-0">
                <p className="text-white font-medium text-sm truncate">{revisione.titolo}</p>
                {revisione.progetto && <p className="text-gray-500 text-xs">{revisione.progetto.titolo}</p>}
              </div>
              <StatusBadge status={revisione.status} />
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Azioni cliente */}
              {!isTeam && revisione.status === 'in_revisione' && (
                <>
                  <button onClick={() => handleStatusChange('approvato')}
                    className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-medium px-3 py-1.5 rounded-lg transition-colors">
                    <CheckCircle2 size={13} /> Approva
                  </button>
                  <button onClick={() => handleStatusChange('richiede_modifiche')}
                    className="flex items-center gap-1.5 bg-red-600/80 hover:bg-red-600 text-white text-xs font-medium px-3 py-1.5 rounded-lg transition-colors">
                    <RotateCcw size={13} /> Modifiche
                  </button>
                </>
              )}
              <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-white"><X size={18} /></button>
            </div>
          </div>

          {/* Media */}
          <div className="flex-1 flex items-center justify-center overflow-hidden relative">
            <Watermark nome={nome} />

            {!url && !isLink ? (
              <div className="w-10 h-10 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
            ) : isVideo ? (
              <video
                ref={videoRef}
                src={url}
                controls
                className="max-w-full max-h-full relative z-0"
                style={{ maxHeight: 'calc(100vh - 120px)' }}
                onTimeUpdate={e => setCurrentTime(e.target.currentTime)}
              />
            ) : isImage ? (
              <img src={url} alt={revisione.titolo} className="max-w-full max-h-full object-contain relative z-0" />
            ) : isPdf ? (
              <iframe src={url} title={revisione.titolo} className="w-full h-full border-0" />
            ) : isLink ? (
              <div className="text-center">
                <LinkIcon size={48} className="text-gray-600 mx-auto mb-4" />
                <a href={revisione.file_url} target="_blank" rel="noopener noreferrer"
                  className="text-violet-400 hover:text-violet-300 underline text-sm break-all">
                  {revisione.file_url}
                </a>
              </div>
            ) : null}
          </div>

          {/* Timecode picker per video */}
          {isVideo && (
            <div className="px-4 py-2 bg-gray-900/80 flex items-center gap-3 flex-shrink-0">
              <span className="text-gray-500 text-xs">Commenta al timecode:</span>
              <button
                onClick={() => setTimecode(timecode !== null ? null : Math.floor(currentTime))}
                className={`text-xs px-2.5 py-1 rounded-lg transition-colors ${timecode !== null ? 'bg-violet-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`}>
                {timecode !== null ? `⏱ ${fmtTime(timecode)}` : `Aggiungi @ ${fmtTime(Math.floor(currentTime))}`}
              </button>
            </div>
          )}
        </div>

        {/* Pannello commenti */}
        <div className="w-80 bg-gray-900 border-l border-gray-800 flex flex-col flex-shrink-0">
          <div className="px-4 py-3 border-b border-gray-800 flex-shrink-0">
            <p className="text-white font-semibold text-sm flex items-center gap-2">
              <MessageSquare size={15} /> Commenti ({commenti.length})
            </p>
            {revisione.descrizione && (
              <p className="text-gray-500 text-xs mt-2 italic">"{revisione.descrizione}"</p>
            )}
          </div>

          {/* Lista commenti */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {commenti.length === 0 && (
              <p className="text-gray-600 text-xs text-center py-6">Nessun commento ancora.</p>
            )}
            {commenti.map(c => {
              const autoreNome = c.autore?.nickname || c.autore?.full_name || c.autore_nome_guest || 'Ospite'
              const isGuest = !c.autore_id
              const isMe = c.autore_id === myId
              return (
                <div key={c.id} className={`${isMe ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
                  {c.timecode_sec != null && (
                    <button
                      onClick={() => handleTimecodeClick(c.timecode_sec)}
                      className="text-[10px] text-violet-400 hover:text-violet-300 transition-colors">
                      ⏱ {fmtTime(c.timecode_sec)}
                    </button>
                  )}
                  <div className={`max-w-[85%] px-3 py-2 rounded-xl text-sm ${isMe ? 'bg-violet-600 text-white rounded-br-sm' : 'bg-gray-800 text-gray-200 rounded-bl-sm'}`}>
                    {!isMe && (
                      <p className="text-xs font-medium mb-1 text-violet-300">
                        {autoreNome}
                        {isGuest && <span className="text-gray-500 font-normal ml-1">(ospite)</span>}
                      </p>
                    )}
                    {editingId === c.id ? (
                      <div className="space-y-1.5">
                        <textarea
                          value={editText}
                          onChange={e => setEditText(e.target.value)}
                          rows={2}
                          autoFocus
                          className="w-full bg-gray-900/50 border border-white/20 rounded-lg px-2 py-1 text-sm text-white focus:outline-none resize-none"
                        />
                        <div className="flex gap-2 justify-end">
                          <button onClick={() => { setEditingId(null); setEditText('') }} className="text-[10px] opacity-70 hover:opacity-100">Annulla</button>
                          <button onClick={() => handleEditComment(c.id)} className="text-[10px] font-medium underline">Salva</button>
                        </div>
                      </div>
                    ) : (
                      <p className="leading-relaxed break-words whitespace-pre-wrap">{renderTextWithLinks(c.testo)}</p>
                    )}
                    <div className={`flex items-center gap-2 mt-1 ${isMe ? 'justify-end' : ''}`}>
                      <span className={`text-[10px] ${isMe ? 'text-violet-200' : 'text-gray-500'}`}>
                        {new Date(c.created_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                      {isMe && editingId !== c.id && (
                        <button
                          onClick={() => { setEditingId(c.id); setEditText(c.testo) }}
                          className="text-[10px] text-violet-200 hover:text-white underline">
                          modifica
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Input commento */}
          <div className="px-4 py-3 border-t border-gray-800 flex-shrink-0">
            {timecode !== null && (
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs text-violet-400 bg-violet-600/10 px-2 py-1 rounded-lg">⏱ {fmtTime(timecode)}</span>
                <button onClick={() => setTimecode(null)} className="text-gray-600 hover:text-gray-400"><X size={12} /></button>
              </div>
            )}
            <div className="flex items-end gap-2">
              <textarea
                value={testo}
                onChange={e => setTesto(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendComment() } }}
                placeholder="Scrivi un commento..."
                rows={1}
                className="flex-1 bg-gray-800 border border-gray-700 rounded-xl px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-violet-500 transition-colors resize-none"
                style={{ height: '38px' }}
              />
              <button
                onClick={handleSendComment}
                disabled={!testo.trim() || sending}
                className="flex-shrink-0 w-9 h-9 bg-violet-600 hover:bg-violet-500 disabled:opacity-40 rounded-xl flex items-center justify-center transition-colors">
                <Send size={14} className="text-white" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ─── Main ─── */
const Revisioni = () => {
  const { profile } = useAuth()
  const [revisioni, setRevisioni] = useState([])
  const [loading, setLoading] = useState(true)
  const [showNuova, setShowNuova] = useState(false)
  const [viewer, setViewer] = useState(null)
  const [filterStatus, setFilterStatus] = useState('all')
  const [copiedId, setCopiedId] = useState(null)
  const [deleting, setDeleting] = useState(null) // revisione in attesa di conferma (soft)
  const [deletingBusy, setDeletingBusy] = useState(false)
  const [trashView, setTrashView] = useState(false)
  const [trashed, setTrashed] = useState([])
  const [emptyConfirm, setEmptyConfirm] = useState(false)
  const [purging, setPurging] = useState(false)
  const myId = profile?.id

  // Soft delete: sposta nel cestino (non cancella file né commenti)
  const handleDelete = async () => {
    if (!deleting) return
    setDeletingBusy(true)
    try {
      const { error } = await supabase.from('revisioni')
        .update({ deleted_at: new Date().toISOString() }).eq('id', deleting.id)
      if (error) throw error
      setRevisioni(prev => prev.filter(r => r.id !== deleting.id))
      setTrashed(prev => [{ ...deleting, deleted_at: new Date().toISOString() }, ...prev])
      setDeleting(null)
    } catch (err) {
      alert('Errore: ' + err.message)
    }
    setDeletingBusy(false)
  }

  // Ripristina dal cestino
  const handleRestore = async (rev) => {
    const { error } = await supabase.from('revisioni').update({ deleted_at: null }).eq('id', rev.id)
    if (!error) {
      setTrashed(prev => prev.filter(r => r.id !== rev.id))
      setRevisioni(prev => [{ ...rev, deleted_at: null }, ...prev]
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at)))
    }
  }

  // Elimina definitivamente una singola revisione dal cestino (con file R2)
  const purgeOne = async (rev) => {
    const { data: versioni } = await supabase
      .from('revisioni').select('file_r2_key')
      .or(`id.eq.${rev.id},revisione_padre_id.eq.${rev.id}`)
    for (const v of (versioni || [])) {
      if (v.file_r2_key) await deleteFile(v.file_r2_key).catch(() => {})
    }
    await supabase.from('revisioni').delete().eq('id', rev.id)
  }

  const handlePurgeOne = async (rev) => {
    await purgeOne(rev)
    setTrashed(prev => prev.filter(r => r.id !== rev.id))
  }

  // Svuota tutto il cestino
  const handleEmptyTrash = async () => {
    setPurging(true)
    try {
      for (const rev of trashed) {
        await purgeOne(rev)
      }
      setTrashed([])
      setEmptyConfirm(false)
    } catch (err) {
      alert('Errore svuotamento cestino: ' + err.message)
    }
    setPurging(false)
  }

  const handleCopyRevLink = (e, token) => {
    e.stopPropagation()
    const url = `${window.location.origin}/r/${token}`
    navigator.clipboard.writeText(url)
    setCopiedId(token)
    setTimeout(() => setCopiedId(null), 2000)
  }

  const fetchRevisioni = useCallback(async () => {
    const { data } = await supabase
      .from('revisioni')
      .select('*, progetto:progetto_id(titolo, colore), inviato:inviato_da(full_name, nickname)')
      .order('created_at', { ascending: false })
    const all = data || []
    setRevisioni(all.filter(r => !r.deleted_at))
    setTrashed(all.filter(r => r.deleted_at))
    setLoading(false)
  }, [])

  useEffect(() => { fetchRevisioni() }, [fetchRevisioni])

  const handleStatusChange = (id, newStatus) => {
    setRevisioni(prev => prev.map(r => r.id === id ? { ...r, status: newStatus } : r))
    if (viewer?.id === id) setViewer(prev => ({ ...prev, status: newStatus }))
  }

  const filtered = filterStatus === 'all' ? revisioni : revisioni.filter(r => r.status === filterStatus)

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">{trashView ? 'Cestino revisioni' : 'Revisioni'}</h1>
          <p className="text-gray-500 text-sm mt-1">
            {trashView ? `${trashed.length} nel cestino` : `${revisioni.length} revisioni totali`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setTrashView(v => !v)}
            className={`flex items-center gap-2 font-medium px-3 py-2.5 rounded-xl transition-colors text-sm border ${trashView ? 'bg-violet-600 border-violet-600 text-white' : 'border-gray-800 text-gray-400 hover:text-white hover:border-gray-700'}`}>
            <Trash2 size={15} />
            {trashView ? 'Torna alle revisioni' : (trashed.length > 0 ? `Cestino (${trashed.length})` : 'Cestino')}
          </button>
          {!trashView && (
            <button
              onClick={() => setShowNuova(true)}
              className="flex items-center gap-2 bg-violet-600 hover:bg-violet-500 text-white font-medium px-4 py-2.5 rounded-xl transition-colors text-sm">
              <Plus size={16} /> Nuova revisione
            </button>
          )}
        </div>
      </div>

      {/* Vista Cestino */}
      {trashView ? (
        <div>
          {trashed.length > 0 && (
            <div className="flex items-center justify-between bg-red-950/20 border border-red-900/40 rounded-xl px-4 py-3 mb-4">
              <p className="text-red-300/80 text-xs">Le revisioni nel cestino si possono ripristinare. Svuotando il cestino verranno eliminate definitivamente (file inclusi).</p>
              <button onClick={() => setEmptyConfirm(true)}
                className="flex-shrink-0 ml-4 flex items-center gap-1.5 text-xs text-red-400 hover:text-red-300 border border-red-900/50 hover:border-red-700 px-3 py-1.5 rounded-lg transition-colors">
                <Trash2 size={13} /> Svuota cestino
              </button>
            </div>
          )}
          {trashed.length === 0 ? (
            <div className="bg-gray-900 border border-gray-800 rounded-2xl p-12 text-center">
              <Trash2 size={36} className="text-gray-700 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">Il cestino è vuoto.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {trashed.map(r => (
                <div key={r.id} className="bg-gray-900 border border-gray-800 rounded-2xl px-5 py-4 flex items-center gap-4">
                  <div className="w-10 h-10 bg-gray-800 rounded-xl flex items-center justify-center flex-shrink-0">
                    {tipoIcon(r.tipo)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-gray-300 font-medium truncate">{r.titolo}</p>
                    <div className="flex items-center gap-2 text-xs text-gray-600">
                      {r.progetto && <span>{r.progetto.titolo}</span>}
                      <span>·</span>
                      <span>eliminata il {new Date(r.deleted_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                    </div>
                  </div>
                  <button onClick={() => handleRestore(r)}
                    className="flex-shrink-0 flex items-center gap-1.5 text-xs text-violet-400 hover:text-violet-300 border border-violet-900/50 hover:border-violet-700 px-3 py-1.5 rounded-lg transition-colors">
                    <RotateCcw size={13} /> Ripristina
                  </button>
                  <button onClick={() => handlePurgeOne(r)}
                    className="flex-shrink-0 text-gray-600 hover:text-red-400 transition-colors p-1.5 rounded-lg hover:bg-gray-800"
                    title="Elimina definitivamente">
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
      <>
      {/* Filtri status */}
      <div className="flex items-center gap-2 mb-5 flex-wrap">
        {[
          { v: 'all', label: 'Tutte' },
          { v: 'in_revisione', label: 'In revisione' },
          { v: 'approvato', label: 'Approvate' },
          { v: 'richiede_modifiche', label: 'Modifiche richieste' },
          { v: 'archiviato', label: 'Archiviate' },
        ].map(({ v, label }) => (
          <button key={v} onClick={() => setFilterStatus(v)}
            className={`text-xs px-3 py-1.5 rounded-full transition-colors ${filterStatus === v ? 'bg-violet-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`}>
            {label}
          </button>
        ))}
      </div>

      {/* Lista */}
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="w-6 h-6 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-12 text-center">
          <Film size={36} className="text-gray-700 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">Nessuna revisione ancora.</p>
          <p className="text-gray-600 text-xs mt-1">Crea la prima revisione per iniziare a raccogliere feedback dai clienti.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(r => (
            <div
              key={r.id}
              onClick={() => setViewer(r)}
              className="bg-gray-900 border border-gray-800 rounded-2xl px-5 py-4 flex items-center gap-4 hover:border-gray-700 transition-colors cursor-pointer group"
            >
              <div className="w-10 h-10 bg-gray-800 rounded-xl flex items-center justify-center flex-shrink-0">
                {tipoIcon(r.tipo)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <p className="text-white font-medium truncate">{r.titolo}</p>
                  {r.versione > 1 && (
                    <span className="text-xs bg-gray-800 text-gray-400 px-1.5 py-0.5 rounded flex-shrink-0">v{r.versione}</span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  {r.progetto && (
                    <span className="flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: r.progetto.colore || '#6d28d9' }} />
                      {r.progetto.titolo}
                    </span>
                  )}
                  <span>·</span>
                  <span>{new Date(r.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                </div>
              </div>
              <StatusBadge status={r.status} />
              {r.public_token && (
                <button
                  onClick={e => handleCopyRevLink(e, r.public_token)}
                  className="flex-shrink-0 flex items-center gap-1.5 text-xs text-gray-500 hover:text-violet-400 transition-colors px-2 py-1 rounded-lg hover:bg-gray-800"
                  title="Copia link pubblico">
                  {copiedId === r.public_token ? <Check size={13} className="text-emerald-400" /> : <Copy size={13} />}
                  <span className="hidden sm:inline">{copiedId === r.public_token ? 'Copiato!' : 'Link'}</span>
                </button>
              )}
              <button
                onClick={e => { e.stopPropagation(); setDeleting(r) }}
                className="flex-shrink-0 text-gray-600 hover:text-red-400 transition-colors p-1.5 rounded-lg hover:bg-gray-800"
                title="Elimina revisione">
                <Trash2 size={15} />
              </button>
              <ChevronRight size={16} className="text-gray-600 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          ))}
        </div>
      )}
      </>
      )}

      {showNuova && (
        <NuovaRevisioneModal
          myId={myId}
          onClose={() => setShowNuova(false)}
          onSaved={fetchRevisioni}
        />
      )}

      {viewer && (
        <ViewerModal
          revisione={viewer}
          myId={myId}
          onClose={() => setViewer(null)}
          onStatusChange={handleStatusChange}
        />
      )}

      {/* Conferma eliminazione */}
      {deleting && (
        <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4" onClick={() => !deletingBusy && setDeleting(null)}>
          <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
            <div className="w-12 h-12 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trash2 size={22} className="text-red-400" />
            </div>
            <h3 className="text-white font-semibold text-center mb-1">Spostare nel cestino?</h3>
            <p className="text-gray-500 text-sm text-center mb-1">"{deleting.titolo}"</p>
            <p className="text-gray-600 text-xs text-center mb-5">La revisione va nel cestino con file e commenti. Potrai ripristinarla o eliminarla definitivamente da lì.</p>
            <div className="flex gap-2">
              <button onClick={() => setDeleting(null)} disabled={deletingBusy}
                className="flex-1 py-2.5 rounded-xl text-sm text-gray-400 hover:text-white hover:bg-gray-800 transition-colors disabled:opacity-40">
                Annulla
              </button>
              <button onClick={handleDelete} disabled={deletingBusy}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-red-600 hover:bg-red-500 text-white text-sm font-medium transition-colors disabled:opacity-40">
                {deletingBusy ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <Trash2 size={14} />}
                Sposta nel cestino
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Conferma svuota cestino */}
      {emptyConfirm && (
        <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4" onClick={() => !purging && setEmptyConfirm(false)}>
          <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
            <div className="w-12 h-12 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trash2 size={22} className="text-red-400" />
            </div>
            <h3 className="text-white font-semibold text-center mb-1">Svuotare il cestino?</h3>
            <p className="text-gray-600 text-xs text-center mb-5">{trashed.length} revisioni verranno eliminate definitivamente, con i loro file e commenti. Operazione irreversibile.</p>
            <div className="flex gap-2">
              <button onClick={() => setEmptyConfirm(false)} disabled={purging}
                className="flex-1 py-2.5 rounded-xl text-sm text-gray-400 hover:text-white hover:bg-gray-800 transition-colors disabled:opacity-40">
                Annulla
              </button>
              <button onClick={handleEmptyTrash} disabled={purging}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-red-600 hover:bg-red-500 text-white text-sm font-medium transition-colors disabled:opacity-40">
                {purging ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <Trash2 size={14} />}
                Svuota definitivamente
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Revisioni
